import { Fragment, useCallback } from "react";
import { ButtonConfirm, CInputControl } from "core/components/form-controls";
import Icon from 'core/assets/icons';
import { AddIngredientByImageSwiperButton } from 'modules/AiChef/components';
import { getFieldInputProps } from 'core/utils/form';
import { useIngredient, useFetchIngredient } from './../../hooks/ingredient';
import useSelectIngredient from './../../hooks/useSelectIngredient';
import IngredientListItem from './ListItem';
import IngredientRequest from './Request';
import IngredientListItemLoading from './ListItemLoading';
import type { TIngredient } from "./../../store/types";
import { _t } from "core/language";

type TIngredientList = {
  myIngredients: TIngredient[];
  onSelectIngradient: (ingredients: TIngredient[]) => void;
}

const IngredientList = ({
  myIngredients,
  onSelectIngradient,
}: TIngredientList) => {
  const { loading, data: ingredientsGlobal } = useFetchIngredient()
  const {
    ingredientSelecting,
    checkIngredientSelected,
    selectIngredient,
  } = useSelectIngredient(ingredientsGlobal)

  const {
    ingredients: ingredientsFiltered,
    formik,

    getExistIngredients,
    message,
  } = useIngredient(ingredientsGlobal, myIngredients, ingredientSelecting)

  const handleSubmit = useCallback(() => {
    const { ids } = getExistIngredients()

    const items = ingredientSelecting.filter((e: TIngredient) => 
      !ids.includes(e.id)
    )
    onSelectIngradient(items)
  }, [getExistIngredients, onSelectIngradient, ingredientSelecting])

  const onRequestSuccess = useCallback((ingredient: TIngredient) => {
    return onSelectIngradient([ingredient])
  }, [onSelectIngradient])

  const isNoFilterData = !loading
    && !ingredientsFiltered.length
    && formik.values.name
  const isShowloading = loading && !ingredientsFiltered.length

  return (
    <Fragment>
      <div className="flex justify-end mb-3.5 px-4">
        <AddIngredientByImageSwiperButton
          hideIcon
          size="sm"
          className="px-[20px] "
        />
      </div>
      <div className="px-4 mb-[14px]">
        <CInputControl
          className=""
          maxLength={200}
          placeholder={_t('ingredient.search_placeholder')}
          classNameIcon="!w-[18px] !h-[18px]"
          classNameInput="!pl-[40px] !text-[14px]"
          icon={Icon.RecipeSearch}
          {...getFieldInputProps(formik, 'name')}
        />
      </div>
      <div className="mb-[60px]">
        {isShowloading && Array.from(Array(10).keys()).map((e: number) => 
          <IngredientListItemLoading key={e} />
        )}
        {isNoFilterData && (
          <div className="relative mt-40">
            <IngredientRequest
              name={formik.values.name}
              onRequestSuccess={onRequestSuccess}
            />
          </div>
        )}
        {ingredientsFiltered.map((ingredient: TIngredient) => (
          <IngredientListItem
            key={ingredient.id}
            ingredient={ingredient}
            isSelected={checkIngredientSelected(ingredient)}
            selectIngredient={selectIngredient}
          />
        ))}
      </div>
      {!isNoFilterData && !isShowloading && ingredientSelecting.length ? (
        <div className="py-3.5 pr-4 fixed bottom-[60px] bg-white w-[calc(100%-16px)]">
          <ButtonConfirm
            label={_t('ingredient.add_total', {number: ingredientSelecting.length})}
            classBtn="w-full "
            handleSubmit={handleSubmit}
            submitText={_t('ingredient.msg.confirm')}
            closeText={_t('ingredient.msg.cancel')}
            message={
              <div
                dangerouslySetInnerHTML={{__html: message}}
                className="text-[14px] font-medium"
              />
            }
          />
        </div>
      ) : null}
    </Fragment>
  )
}

export default IngredientList
